import { useCallback } from 'react';
import {
	getEmptyUnlayerDesign,
	mergeRecipientsByEmail,
} from '@/components/Drawers/AddUnlayerEmailDrawer/helpers';
import {
	SCOPE_OF_ADVICE_TOKEN,
	applyTokenReplacementsToDesign,
	designContainsScopeOfAdviceToken,
	injectScopeOfAdviceTableIntoDesign,
} from '@/components/Drawers/AddUnlayerEmailDrawer/hooks/unlayerDesignMutations';
import { useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { useUnlayerUIContext } from '@/context/UnlayerUIContext';
import { useUnlayerTemplateContext } from '@/context/UnlayerTemplateContext';
import { useUnlayerDraftContext } from '@/context/UnlayerDraftContext';
import { useUnlayerSendContext } from '@/context/UnlayerSendContext';
import { useUnlayerMergeTagsContext } from '@/context/UnlayerMergeTagsContext';

/**
 * Groups the non-trivial event handlers that coordinate Formik state with the
 * draft/template workflows and the editor-specific local UI state.
 */
export const useComposeFormInteractions = () => {
	const { clearSendError } = useUnlayerSendContext();
	const { buildScopeOfAdviceInjectionOptions } = useUnlayerMergeTagsContext();
	const dispatch = useDispatch();
	const { handleTemplateSelect: loadTemplateDetail } = useUnlayerTemplateContext();
	const draftActions = useUnlayerDraftContext();
	const {
		closeLoadDesignDialog,
		loadDesignJsonError,
		loadDesignJsonValue,
		markProgrammaticDesignLoaded,
		openSaveDialog,
		setDesignToLoad,
		setLoadDesignJsonError,
		setLoadDesignJsonValue,
	} = useUnlayerUIContext();

	const { values, setFieldValue, setFieldTouched, validateForm, setTouched, setErrors } =
		useFormikContext();

	const selectedClientId = values.client?.clientId ?? null;

	const onClose = () => dispatch(closeDialog('unlayerEmail'));

	const handleClose = useCallback(() => {
		clearSendError();
		onClose();
	}, [clearSendError, onClose]);

	// Draft and template loads only replace the subject and editor design.
	// Recipient and folder metadata stay under the user's current control.
	const applyLoadedDesign = useCallback(
		(detail) => {
			setFieldValue('subject', detail?.subject ?? '');
			setFieldValue('editorDesign', detail?.designJson ?? null);
			setDesignToLoad(detail?.designJson ?? null);

			if (detail?.attachments?.length) {
				const attachmentFiles = detail.attachments.map((attachment) => ({
					name: attachment.fileName,
					size: attachment.fileSize,
					url: attachment.downloadUrl,
					downloadUrl: attachment.downloadUrl,
					mimeType: attachment.mimeType ?? attachment.type,
				}));
				setFieldValue('files', attachmentFiles);
			} else {
				setFieldValue('files', []);
			}
		},
		[setDesignToLoad, setFieldValue],
	);

	// Client changes alter the draft scope. We also seed recipients from the client,
	// reset the deal field, and drop the selected draft if it no longer belongs to
	// the active client context.
	const handleClientChange = useCallback(
		async (value) => {
			const nextRecipients = mergeRecipientsByEmail(values.to, value);
			const nextClientId = value?.clientId ?? null;
			setFieldValue('client', value);
			setFieldValue('to', nextRecipients);
			setFieldValue('deal', null);
			draftActions.setDraftActionError('');
			draftActions.setPendingDraftSelectionId('');

			if (nextClientId) {
				const refreshedDrafts = await draftActions.reloadDraftSummaries({
					clientId: nextClientId,
					dealId: null,
				});
				if (
					draftActions.selectedDraftId &&
					!refreshedDrafts.some((draft) => draft.id === draftActions.selectedDraftId)
				) {
					draftActions.clearDraftSelection();
				}
			} else {
				draftActions.setHasLoadedDraftSummaries(false);
				draftActions.clearDraftSelection();
			}

			setFieldTouched('client', true, true);
		},
		[draftActions, setFieldTouched, setFieldValue, values.to],
	);

	// Deal changes can further narrow the draft scope. If the selected draft is not
	// returned in the refreshed summaries for the new scope, we clear it locally.
	const handleDealChange = useCallback(
		async (value) => {
			setFieldValue('deal', value);
			draftActions.setDraftActionError('');
			draftActions.setPendingDraftSelectionId('');
			const nextDealId = value?.dealId ?? null;

			if (selectedClientId || nextDealId) {
				const refreshedDrafts = await draftActions.reloadDraftSummaries({
					clientId: selectedClientId,
					dealId: nextDealId,
				});
				if (
					draftActions.selectedDraftId &&
					!refreshedDrafts.some((draft) => draft.id === draftActions.selectedDraftId)
				) {
					draftActions.clearDraftSelection();
				}
			}
		},
		[draftActions, selectedClientId, setFieldValue],
	);

	// Templates and drafts both drive editor content. Selecting a template clears
	// any active draft first so the UI has a single source of truth.
	const handleTemplateSelect = useCallback(
		async (templateId, options = {}) => {
			if (draftActions.selectedDraftId) {
				draftActions.clearDraftSelection();
			}

			if (!templateId) {
				await loadTemplateDetail(templateId);
				const emptyDesign = getEmptyUnlayerDesign();

				setDesignToLoad(emptyDesign);
				setFieldValue('subject', '');
				setFieldValue('editorDesign', emptyDesign);
				setFieldValue('files', []);
				return null;
			}

			const detail = await loadTemplateDetail(templateId);
			if (!detail) return null;
			let tokenReplacements = options?.tokenReplacements ?? {};
			let scopeOfAdviceHtmlBlocks = Array.isArray(options?.scopeOfAdviceHtmlBlocks)
				? options.scopeOfAdviceHtmlBlocks.filter(
						(html) => typeof html === 'string' && html.length > 0,
					)
				: [];
			const hasExplicitScopeReplacement =
				typeof tokenReplacements?.[SCOPE_OF_ADVICE_TOKEN] === 'string' &&
				tokenReplacements[SCOPE_OF_ADVICE_TOKEN].length > 0;
			if (
				!hasExplicitScopeReplacement &&
				typeof buildScopeOfAdviceInjectionOptions === 'function' &&
				designContainsScopeOfAdviceToken(detail?.designJson)
			) {
				const autoScopeInjectionOptions =
					(await buildScopeOfAdviceInjectionOptions()) ?? {};
				tokenReplacements = {
					...tokenReplacements,
					...(autoScopeInjectionOptions.tokenReplacements ?? {}),
				};
				scopeOfAdviceHtmlBlocks = Array.isArray(
					autoScopeInjectionOptions.scopeOfAdviceHtmlBlocks,
				)
					? autoScopeInjectionOptions.scopeOfAdviceHtmlBlocks.filter(
							(html) => typeof html === 'string' && html.length > 0,
						)
					: scopeOfAdviceHtmlBlocks;
			}
			const scopeOfAdviceTableHtml = tokenReplacements?.[SCOPE_OF_ADVICE_TOKEN];
			const tokenReplacementsWithoutScope = Object.entries(tokenReplacements).reduce(
				(acc, [token, replacement]) => {
					if (token === SCOPE_OF_ADVICE_TOKEN) return acc;
					acc[token] = replacement;
					return acc;
				},
				{},
			);
			let nextDesign = detail?.designJson ?? null;
			if (Object.keys(tokenReplacementsWithoutScope).length > 0) {
				nextDesign = applyTokenReplacementsToDesign(
					nextDesign,
					tokenReplacementsWithoutScope,
				);
			}
			if (typeof scopeOfAdviceTableHtml === 'string' && scopeOfAdviceTableHtml.length > 0) {
				const scopeTableInjectionResult = injectScopeOfAdviceTableIntoDesign(
					nextDesign,
					scopeOfAdviceHtmlBlocks.length > 0
						? scopeOfAdviceHtmlBlocks
						: [scopeOfAdviceTableHtml],
				);
				nextDesign = applyTokenReplacementsToDesign(scopeTableInjectionResult.design, {
					[SCOPE_OF_ADVICE_TOKEN]: scopeOfAdviceTableHtml,
				});
			}
			setDesignToLoad(nextDesign);
			setFieldValue('editorDesign', nextDesign);
			if (typeof detail?.subject === 'string') {
				setFieldValue('subject', detail.subject);
			}
			setFieldValue(
				'files',
				(detail.attachments ?? []).map((attachment) => ({
					name: attachment.fileName,
					size: attachment.fileSize,
					url: attachment.downloadUrl,
					downloadUrl: attachment.downloadUrl,
					mimeType: attachment.mimeType ?? attachment.type,
				})),
			);
			return {
				...detail,
				designJson: nextDesign,
			};
		},
		[
			buildScopeOfAdviceInjectionOptions,
			draftActions,
			loadTemplateDetail,
			setDesignToLoad,
			setFieldValue,
		],
	);

	// Draft selection is guarded by dirty-state checks so we do not replace unsaved
	// editor work without an explicit user confirmation.
	const handleDraftSelect = useCallback(
		async (draftId) => {
			// If no draft selected ('' value from the dropdown), clear the selection
			// and load an empty editor design so the editor shows a blank document.
			if (!draftId) {
				draftActions.clearDraftSelection();
				const emptyDesign = getEmptyUnlayerDesign();
				setFieldValue('subject', '');
				setFieldValue('editorDesign', emptyDesign);
				setDesignToLoad(emptyDesign);
				markProgrammaticDesignLoaded();
				return;
			}

			const draftDetail = await draftActions.loadDraftIntoCompose(draftId);

			if (draftDetail) {
				setFieldValue('subject', draftDetail.subject);

				if (draftDetail.designJson) {
					setFieldValue('editorDesign', draftDetail.designJson);
					setDesignToLoad(draftDetail.designJson ?? null);
				}

				if (draftDetail?.attachments?.length) {
					const attachmentFiles = draftDetail.attachments.map((attachment) => ({
						name: attachment.fileName,
						size: attachment.fileSize,
						url: attachment.downloadUrl,
						downloadUrl: attachment.downloadUrl,
						mimeType: attachment.mimeType ?? attachment.type,
					}));
					setFieldValue('files', attachmentFiles);
				} else {
					setFieldValue('files', []);
				}
				markProgrammaticDesignLoaded();
			}
		},
		[draftActions, markProgrammaticDesignLoaded, setDesignToLoad, setFieldValue],
	);

	// After the user confirms replacement, consume the pending draft id and load it
	// into the form as the new active editor state.
	const handleReplaceDraft = useCallback(async () => {
		const nextDraftId = draftActions.pendingDraftSelectionId;
		draftActions.setIsDraftReplaceDialogOpen(false);
		draftActions.setPendingDraftSelectionId('');
		if (!nextDraftId) return;

		try {
			const detail = await draftActions.loadDraftIntoCompose(nextDraftId);
			if (detail) {
				applyLoadedDesign(detail);
			}
		} catch (error) {
			draftActions.setDraftActionError(
				error?.data?.message ?? 'Unable to load selected draft.',
			);
		}
	}, [applyLoadedDesign, draftActions]);

	// Raw design JSON loads are intentionally strict: only valid object payloads are
	// allowed because the editor expects a full Unlayer design structure.
	const handleLoadDesignJson = useCallback(() => {
		let parsedDesign = null;
		try {
			parsedDesign = JSON.parse(loadDesignJsonValue);
		} catch (error) {
			setLoadDesignJsonError('Design JSON must be valid JSON.');
			return;
		}

		if (!parsedDesign || typeof parsedDesign !== 'object') {
			setLoadDesignJsonError('Design JSON must be a JSON object.');
			return;
		}

		setLoadDesignJsonError('');
		setDesignToLoad(parsedDesign);
		setFieldTouched('editorHtml', true, true);
		closeLoadDesignDialog();
	}, [
		closeLoadDesignDialog,
		loadDesignJsonValue,
		setDesignToLoad,
		setFieldTouched,
		setLoadDesignJsonError,
	]);

	// Persist every editor change back into Formik so validation, draft saves, and
	// final send all operate on the latest editor snapshot.
	const handleEditorChange = useCallback(
		(payload) => {
			setFieldValue('editorHtml', payload?.html ?? '');
			setFieldValue('editorDesign', payload?.design ?? null);
			setFieldTouched('editorHtml', true, false);
		},
		[setFieldTouched, setFieldValue],
	);

	// Sending is a two-step interaction: validate first, then open the save/send
	// confirmation flow only when the form is currently valid.
	const handleValidateAndOpenSendDialog = useCallback(() => {
		clearSendError();
		validateForm().then((errorValues) => {
			if (Object.keys(errorValues).length === 0) {
				openSaveDialog();
				return;
			}

			const touchedValues = Object.entries(errorValues).reduce((acc, [key, value]) => {
				acc[key] = Boolean(value);
				return acc;
			}, {});
			setTouched(touchedValues);
			setErrors(errorValues);
		});
	}, [clearSendError, openSaveDialog, setErrors, setTouched, validateForm]);

	// Typing in the raw design JSON modal should clear stale parse errors once the
	// user starts editing the payload again.
	const handleLoadDesignJsonValueChange = useCallback(
		(value) => {
			setLoadDesignJsonValue(value, { clearError: Boolean(loadDesignJsonError) });
		},
		[loadDesignJsonError, setLoadDesignJsonValue],
	);

	return {
		handleClientChange,
		handleClose,
		handleDealChange,
		handleDraftSelect,
		handleEditorChange,
		handleLoadDesignJson,
		handleLoadDesignJsonValueChange,
		handleReplaceDraft,
		handleTemplateSelect,
		handleValidateAndOpenSendDialog,
	};
};
